import React, { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { NAV, AUTH } from "@/constants/testIds";
import {
  FolderLock, Users, Settings, Sun, Moon, LogOut, Bell, ShieldCheck, Activity,
  Shield, KeyRound, ListTodo, ThumbsUp,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "@/components/Toaster";
import { BUILD_VERSION } from "@/lib/version";
import MadeBySSPL from "@/components/MadeBySSPL";

const navLink = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 text-sm rounded-md transition-colors ${
    isActive
      ? "bg-primary text-primary-foreground"
      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
  }`;

function notifLink(n) {
  // Prefer backend-provided link, else derive from room_id.
  if (n.link) return n.link;
  if (n.room_id) {
    const tab = n.kind === "qa" ? "qa"
      : n.kind === "member" ? "members"
      : n.kind === "document" ? "documents"
      : null;
    return tab ? `/app/rooms/${n.room_id}?tab=${tab}` : `/app/rooms/${n.room_id}`;
  }
  return null;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);
  // Track which notification IDs we've already toasted so polling doesn't
  // spam the screen on every refresh. Seed from initial load (don't toast
  // notifications that already existed before the user signed in).
  const seenIds = useRef(null);
  const unread = notifs.filter((n) => !n.read).length;

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        setNotifs(data);
        if (seenIds.current === null) {
          // First load: don't toast any pre-existing notification.
          seenIds.current = new Set(data.map((n) => n.id));
        } else {
          const fresh = data.filter((n) => !seenIds.current.has(n.id) && !n.read);
          for (const n of fresh.slice(0, 3)) {
            const url = notifLink(n);
            toast.info(n.text, {
              duration: 5000,
              action: url ? {
                label: "View",
                onClick: () => navigate(url),
              } : undefined,
            });
            seenIds.current.add(n.id);
          }
          // Always sync the seen set so reads etc. don't re-trigger toasts.
          for (const n of data) seenIds.current.add(n.id);
        }
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close popover when clicking outside.
  useEffect(() => {
    if (!showNotifs) return;
    const onDocClick = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setShowNotifs(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showNotifs]);

  const markAllRead = async () => {
    await api.post("/notifications/read");
    setNotifs((arr) => arr.map((n) => ({ ...n, read: true })));
  };

  const clearAll = async () => {
    try {
      await api.delete("/notifications");
      setNotifs([]);
    } catch { /* ignore */ }
  };

  const openNotif = async (n) => {
    const url = notifLink(n);
    // Optimistically mark this single notification read
    if (!n.read) {
      setNotifs((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    setShowNotifs(false);
    if (url) navigate(url);
  };

  const isAdmin = user?.role === "admin";
  const isOwner = !!user?.owner;

  // Pending access-request count drives the sidebar badge for App Owners.
  const [pendingAccess, setPendingAccess] = useState(0);
  useEffect(() => {
    if (!isOwner) return undefined;
    let alive = true;
    const ping = async () => {
      try {
        const { data } = await api.get("/access-requests/pending-count");
        if (alive) setPendingAccess(data.count || 0);
      } catch { /* ignore */ }
    };
    ping();
    const id = setInterval(ping, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [isOwner]);

  // Tasks-feature access flag — drives whether the "Tasks" nav entry is
  // rendered. Owners/admins always have access; regular users only when
  // an Owner has explicitly granted it.
  const [tasksAccess, setTasksAccess] = useState(false);
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/tasks/access/me");
        if (alive) setTasksAccess(!!data.has_access);
      } catch {
        if (alive) setTasksAccess(false);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  return (
    <div className="h-screen overflow-hidden grid grid-cols-1 md:grid-cols-[260px_1fr]">
      {/* Sidebar */}
      <aside className="border-r border-border bg-card flex flex-col h-screen overflow-hidden">
        <div className="px-6 py-6 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <div className="font-display text-base font-medium leading-none">Sovereign</div>
              <div className="label-mono text-muted-foreground mt-1">VDR · v{BUILD_VERSION}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          <div className="label-mono text-muted-foreground px-4 mb-2">— Workspace</div>
          <NavLink to="/app/rooms" data-testid={NAV.toRooms} className={navLink}>
            <FolderLock className="w-4 h-4" /> Data Rooms
          </NavLink>
          {tasksAccess && (
            <NavLink to="/app/tasks" data-testid="nav-tasks" className={navLink}>
              <ListTodo className="w-4 h-4" /> Tasks
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/app/users" data-testid={NAV.toUsers} className={navLink}>
              <Users className="w-4 h-4" /> Users
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/app/activity" data-testid="nav-activity" className={navLink}>
              <Activity className="w-4 h-4" /> Activity
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/app/ada-feedback" data-testid="nav-ada-feedback" className={navLink}>
              <ThumbsUp className="w-4 h-4" /> Ada Feedback
            </NavLink>
          )}
          {isOwner && (
            <NavLink
              to="/app/owner/access-requests"
              data-testid="nav-owner-access"
              className={navLink}
            >
              <Shield className="w-4 h-4 text-emerald-500" />
              <span className="flex-1">Access Requests</span>
              {pendingAccess > 0 && (
                <span
                  data-testid="nav-owner-access-badge"
                  className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-emerald-500 text-emerald-950 text-[10px] font-semibold"
                >
                  {pendingAccess > 99 ? "99+" : pendingAccess}
                </span>
              )}
            </NavLink>
          )}
          {!isOwner && (
            <NavLink
              to="/app/access-requests"
              data-testid="nav-request-access"
              className={navLink}
            >
              <KeyRound className="w-4 h-4" /> Request Access
            </NavLink>
          )}
          <div className="label-mono text-muted-foreground px-4 mt-6 mb-2">— Account</div>
          <NavLink to="/app/settings" data-testid={NAV.toSettings} className={navLink}>
            <Settings className="w-4 h-4" /> Settings
          </NavLink>
        </nav>

        <div className="border-t border-border p-4 space-y-3 flex-shrink-0">
          <div className="text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="presence-dot flex-shrink-0"
                role="status"
                aria-label="Online"
                title="Online"
                data-testid="user-presence-dot"
              />
              <span className="font-medium truncate">{user?.name || user?.email}</span>
              {isOwner && (
                <span
                  data-testid="user-owner-badge"
                  className="inline-flex items-center gap-0.5 mono uppercase tracking-wider text-[9px] px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 flex-shrink-0"
                  title="App Owner"
                >
                  <Shield className="w-2.5 h-2.5" /> Owner
                </span>
              )}
            </div>
            {/* Email under the name so users can confirm which account
                they're signed in to without opening Settings. */}
            {user?.email && user.email !== user?.name && (
              <div
                data-testid="sidebar-user-email"
                className="text-muted-foreground truncate text-[11px] mt-0.5 mono"
                title={user.email}
              >
                {user.email}
              </div>
            )}
            <div className="text-muted-foreground truncate mono mt-0.5 text-[10px] uppercase tracking-wider">
              {isOwner ? "App Owner" : user?.role}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              data-testid={NAV.themeToggle}
              onClick={toggle}
              className="flex-1 inline-flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-md border border-border hover:bg-secondary transition"
            >
              {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
              {theme === "dark" ? "Light" : "Dark"}
            </button>
            <button
              data-testid={AUTH.logout}
              onClick={async () => { await logout(); navigate("/login"); }}
              className="flex-1 inline-flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-md border border-border hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition"
              title="Sign out of Sovereign VDR"
              aria-label="Sign out"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
          <MadeBySSPL align="center" className="mt-3 block text-[10px]" />
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col h-screen overflow-hidden">
        <header className="flex items-center justify-between px-8 py-4 border-b border-border flex-shrink-0 relative z-40">
          <div className="flex items-center gap-3">
            <span className="label-mono text-muted-foreground">— Sovereign Data Launchpad</span>
            <span className="hidden md:inline label-mono text-muted-foreground">/ Tier-1 / AU</span>
          </div>
          <div className="relative">
            <button
              ref={btnRef}
              data-testid={NAV.notificationsBtn}
              onClick={() => setShowNotifs((v) => !v)}
              className="relative p-2 rounded-md border border-border hover:bg-secondary transition"
            >
              <Bell className="w-4 h-4" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {unread}
                </span>
              )}
            </button>
            {showNotifs && (
              <div
                ref={popRef}
                data-testid="notifications-popover"
                className="absolute right-0 top-12 w-80 bg-popover border border-border rounded-md shadow-xl z-[100]"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-border gap-3">
                  <span className="label-mono">Notifications</span>
                  <div className="flex items-center gap-3">
                    <button
                      data-testid="notifications-mark-read"
                      onClick={markAllRead}
                      disabled={unread === 0}
                      className="text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-default"
                    >
                      Mark all read
                    </button>
                    <span className="w-px h-3 bg-border" aria-hidden />
                    <button
                      data-testid="notifications-clear-all"
                      onClick={clearAll}
                      disabled={notifs.length === 0}
                      className="text-xs text-rose-500 hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-default"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
                <div className="max-h-96 overflow-auto">
                  {notifs.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-muted-foreground">All clear.</div>
                  ) : notifs.map((n) => {
                    const url = notifLink(n);
                    const clickable = !!url;
                    return (
                      <button
                        key={n.id}
                        data-testid="notification-row"
                        onClick={() => openNotif(n)}
                        disabled={!clickable}
                        className={`w-full text-left px-4 py-3 border-b border-border transition ${
                          clickable ? "hover:bg-secondary cursor-pointer" : "cursor-default"
                        } ${n.read ? "opacity-60" : ""}`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm">{n.text}</div>
                            <div className="mono text-[10px] text-muted-foreground mt-1">
                              {new Date(n.ts).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
